// src/components/layout/Navbar.tsx
import { useEffect, useRef, useState } from "react";
import { FaBookOpen, FaChevronDown } from "react-icons/fa";
import { RiLogoutCircleRLine } from "react-icons/ri";
import { useAppNavigation } from "../routes/useAppNavigation";
import { useAuth } from "../context/AuthContext";
import { useBrand } from "../context/brandContext";
import type { Brand } from "../shared/types";
import { ServiceCode } from "../constants/enum";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { IoTelescope } from "react-icons/io5";
import TenantChat from "../features/chat/tenantChats";
import { UserAvatar } from "../shared/ui/UserAvatar";
import { getTenantById } from "../shared/services/apis/tenant";

interface Tenant {
  id: string;
  name: string;
  logoUrl?: string;
}

const Navbar = () => {
  const { tenantId } = useParams<{ tenantId: string }>();

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isStateTenantId, setIsStateTenantId] = useState<string>();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const brand = useBrand();
  const { navigateTo, ROUTES } = useAppNavigation();
  const { logout, user } = useAuth();
  const location = useLocation();
  const chatData = location?.state?.chatData;
  const navigate =useNavigate();

  const [tenant, setTenant] = useState<Tenant | null>();

  useEffect(() => {
    if (!tenantId) return;

    const fetchTenant = async () => {
      try {
        const data = await getTenantById(tenantId);
        setTenant(data);
      } catch (error) {
        console.error("Error fetching tenant:", error);
      }
    };

    fetchTenant();
  }, [tenantId]);

  if (chatData) {
    useEffect(() => {
      setIsStateTenantId(chatData.tenantId);
    }, [isStateTenantId]);
  }

  useEffect(() => {
    if (!isStateTenantId) return;

    const fetchTenant = async () => {
      try {
        const data = await getTenantById(isStateTenantId);
        setTenant(data);
      } catch (error) {
        console.error("Error fetching tenant:", error);
      }
    };
    fetchTenant();
  }, [isStateTenantId]);

  const handleTabClick = (service: NonNullable<Brand["services"]>[0]) => {
    if (service.service?.code === ServiceCode.MATRIX) {
      navigateTo(ROUTES.MATRIX);
    } else if (service.service?.code === ServiceCode.DEEP_SEARCH) {
      navigateTo(ROUTES.DEEP_SEARCH);
    }
  };
  const getRouteByService = (code?: string) => {
    switch (code) {
      case ServiceCode.MATRIX:
        return ROUTES.MATRIX;
      case ServiceCode.DEEP_SEARCH:
        return ROUTES.DEEP_SEARCH;
      default:
        return "";
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      setIsDropdownOpen(false);
      navigateTo(ROUTES.LOGIN);
    } catch (error) {
      console.error("Logout failed:", error);
      // Still redirect to login even if logout fails
      navigateTo(ROUTES.LOGIN);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <nav className="bg-[#FDFDFD] sticky top-0 z-50 mb-2">
      <div className="flex justify-between items-center px-6 py-2">
        <button
          onClick={()=>navigate("/")}
          style={{
            fontFamily: "'Didot', 'Bodoni MT', 'Playfair Display', serif",
          }}
          className="text-xl uppercase font-bold tracking-wide text-gray-800 hidden md:flex cursor-pointer"
        >
          {brand.brand?.name}
        </button>
        {/* CENTER: Dynamic Tabs using route config */}
        {tenantId || isStateTenantId ? (
          <div className="flex items-center gap-2">
            <img
              src={tenant?.logoUrl}
              onError={(e) => {
                // If logo URL fails, fall back to initials
                e.currentTarget.src = `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(
                  tenant?.name || "Unknown"
                )}`;
              }}
              alt={brand.brand?.name || "Brand"}
              className="w-7 h-7 rounded-full border border-gray-300"
            />

            <span className="text-md  capitalize font-light text-gray-800">
              {tenant?.name}
            </span>
            {/* <FaChevronDown size={10} className="text-gray-400" /> */}
          </div>
        ) : (
          <div className="hidden md:flex items-center bg-gray-100 rounded-xl shadow-inner p-1">
            {brand.brand?.services?.slice().reverse().map((tab) => {
              const code = tab.service?.code;
              const currentRoute = getRouteByService(code);
              const isActive =
                location.pathname.startsWith(currentRoute) ||
                (location.pathname === "/" && code === ServiceCode.MATRIX);
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabClick(tab)}
                  className={`flex capitalize items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition cursor-pointer ${
                    isActive
                      ? "bg-[#FDFDFD] shadow text-blue-600"
                      : "text-gray-500 hover:bg-gray-200"
                  }`}
                >
                  {isActive && tab.service?.code === ServiceCode.MATRIX && (
                    <FaBookOpen className="text-blue-600" />
                  )}
                  {isActive &&
                    tab.service?.code === ServiceCode.DEEP_SEARCH && (
                      <IoTelescope className="text-blue-600" />
                    )}

                  {tab.service?.name}
                </button>
              );
            })}
          </div>
        )}

        {/* Mobile menu toggle */}
        <button
          className="md:hidden p-2 text-gray-600 focus:outline-none"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </button>

        {/* RIGHT: Avatar & AI */}

        <div className="hidden md:flex items-center ">
          {tenantId && <TenantChat />}
          <div
            ref={dropdownRef}
            className=" relative  flex items-center bg-white shadow-md rounded-xl px-4 py-2 cursor-pointer"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          >
            <UserAvatar
              name={user?.fullName || "Unknown User"}
              imageUrl={""}
              size="xs"
              className="mr-2"
            />
            <FaChevronDown className="ml-2 border-l-2 border-gray-300 pl-2 h-5 w-5 text-gray-500 text-sm" />

            {isDropdownOpen && (
              <div className="absolute right-0 top-12 min-w-22 border-t border-gray-200 bg-white shadow-lg rounded-b-xl z-50">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Logout <RiLogoutCircleRLine className="ml-2" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden mt-2 px-4 pb-4 space-y-2 animate-slide-down transition-all duration-300 ease-in-out bg-gray-100 rounded-lg shadow">
          {brand.brand?.services?.map((tab) => {
            const code = tab.service?.code;
            const currentRoute = getRouteByService(code);
            const isActive =
              location.pathname.startsWith(currentRoute) ||
              (location.pathname === "/" && code === ServiceCode.MATRIX);

            return (
              <button
                key={tab.id}
                onClick={() => {
                  handleTabClick(tab);
                  setIsMobileMenuOpen(false);
                }}
                className={`flex items-center w-full justify-center px-4 py-3 rounded-lg font-medium transition-colors duration-200 gap-x-3 cursor-pointer${
                  isActive
                    ? "bg-[#FDFDFD] shadow text-blue-600"
                    : "text-gray-600 hover:bg-gray-200"
                }`}
              >
                {/* Show active icon */}
                {isActive && code === ServiceCode.MATRIX && (
                  <FaBookOpen className="text-blue-600 h-4 w-4" />
                )}
                {isActive && code === ServiceCode.DEEP_SEARCH && (
                  <IoTelescope className="text-blue-600 h-4 w-4" />
                )}
                <span>{tab.service?.name}</span>
              </button>
            );
          })}

          {/* Logout */}
          <div className="border-t border-gray-200 mt-3 pt-2">
            <button
              onClick={handleLogout}
              className="flex items-center w-full justify-between px-4 py-2 text-sm text-gray-700 rounded-md hover:bg-gray-200 transition duration-200"
            >
              <span>Logout</span>
              <RiLogoutCircleRLine className="text-lg text-gray-500 ml-2" />
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
